import * as React from 'react';

import { StoreApi, useStore } from 'zustand';

import { IconRegularClose } from '@notino/react-styleguide';
import { usePrevious } from '@notino/react-web-utils';

import { OverflowFade } from '@components/OverflowFade';
import { useUpdateEffect } from '@hooks';
import { styled } from '@linaria/react';

type Props = {
  store: StoreApi<{
    selectedItems: Array<{
      name: string;
      navigationValue: number;
    }>;
  }>;
};

export const FooterSelectedItems = ({ store }: Props) => {
  const selectedItems = useStore(store, (x) => x.selectedItems);
  const hasItems = selectedItems.length > 0;
  const prevSelectedItems = usePrevious(selectedItems);
  const scrollableElRef = React.useRef<HTMLDivElement>(null);

  useUpdateEffect(() => {
    if ((prevSelectedItems?.length ?? 0) >= selectedItems.length) {
      return;
    }
    scrollableElRef.current?.scrollTo({
      left: scrollableElRef.current.scrollWidth,
      behavior: 'smooth',
    });
  }, [selectedItems, prevSelectedItems]);

  if (!hasItems) return null;

  const handleUnselect = (navigationValue: number) => {
    store.setState({
      selectedItems: selectedItems.filter(
        (x) => x.navigationValue !== navigationValue
      ),
    });
  };

  return (
    <OverflowFade
      deps={[selectedItems]}
      config={{ showArrowCalulationOffset: 20 }}
      scrollableElRef={scrollableElRef}
    >
      <FooterSelectedItemsWrapper>
        {selectedItems.map((item) => (
          <FooterSelectedItem
            key={item.navigationValue}
            onClick={() => handleUnselect(item.navigationValue)}
          >
            {item.name}
            <IconRegularClose width="12px" height="12px" />
          </FooterSelectedItem>
        ))}
      </FooterSelectedItemsWrapper>
    </OverflowFade>
  );
};

const FooterSelectedItemsWrapper = styled.div`
  width: 100%;
  display: flex;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border-bottom: 1px solid #f5f5f5;

  &:after {
    content: '';
    padding-inline: 0.375em;
  }
`;

export const FooterSelectedItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  background-color: #f5f5f5;
  white-space: nowrap;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  line-height: 1.5rem;
  letter-spacing: 0px;
  border-radius: 0.125rem;
  user-select: none;
  cursor: pointer;
`;
